import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { bool, object, oneOfType, string } from "prop-types";
import cx from "classnames";

import { useStatic, usePortal, useUniqueId } from "../../utils/hooks";
import canUseDom from "../../utils/canUseDom";

import TooltipStatic from "./Tooltip.static";

const CLASS_ROOT = "tooltip";

const propTypes = {
  /** Tooltip dialog content */
  dialog: oneOfType([string, object]),
  /** Class names applied to dialog */
  dialogClassName: string,
  /** Tooltip ID */
  id: string,
  /** Use a div to wrap the tooltip trigger. Default is span. */
  isBlockElement: bool,
};

const Tooltip = ({
  children,
  dialog,
  dialogClassName,
  id,
  className,
  isBlockElement,
  ...other
}) => {
  const currentId = useUniqueId(id);
  const target = usePortal("root-tooltips");

  const [tooltipRef, instance] = useStatic(TooltipStatic, {
    portal: false,
  });

  // FIXME:
  // When Tooltip is in Modal (eg. parent component is using React Portal),
  // we are not able to get the reference on trigger in useStatic hook, because
  // trigger is in limbo (portaling and not in do DOM yet). Dunno how to fix
  // this better. Async helps.
  useEffect(() => {
    setTimeout(() => {
      if (instance.current && !instance.current?.trigger) {
        instance.current.init();
      }
    });
  });

  function getTrigger() {
    if (React.Children.count(children) === 1) {
      if (typeof children === "string") {
        return wrapTrigger(children);
      }
      return React.cloneElement(children, {
        tabIndex: 0,
        "aria-describedby": currentId,
        "aria-owns": currentId, // helps NVDA speak the description text
        ...other,
      });
    }
    return wrapTrigger(children);
  }

  function wrapTrigger(content) {
    const Tag = isBlockElement ? "div" : "span";

    return (
      <Tag
        key="tooltipTrigger"
        tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        className={className}
        aria-describedby={currentId}
        aria-owns={currentId} // helps NVDA speak the description text
        {...other}
      >
        {content}
      </Tag>
    );
  }

  const dialogClasses = cx(CLASS_ROOT, dialogClassName);

  const render = (
    <span
      key="tooltipDialog"
      id={currentId}
      className={dialogClasses}
      role="tooltip"
      aria-hidden="true"
      ref={tooltipRef}
      data-tooltip
    >
      <span className="tooltip__inner">
        <span className="tooltip__arrow" data-popper-arrow />
        <span className="tooltip__content">{dialog}</span>
      </span>
    </span>
  );

  return (
    <>
      {getTrigger()}
      {canUseDom() && target ? createPortal(render, target) : render}
    </>
  );
};

Tooltip.propTypes = propTypes;
Tooltip.displayName = "Tooltip";

export default Tooltip;
